import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Target,
  TrendingUp,
  Users,
  DollarSign,
  AlertTriangle,
  Trophy,
  X,
  Zap
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import CelebrationAnimation from '../../components/animations/CelebrationAnimation';
import { StepResultForm } from '../../components/betting';

const BettingManagement = () => {
  const [showResultModal, setShowResultModal] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);

  const queryClient = useQueryClient();


  const { data: bettingData, isLoading, error } = useQuery(
    'current-bet-total',
    () => api.get('/betting/current-bet-total').then(res => res.data),
    {
      // ⚠️ Refetch automático solo cuando el modal NO esté abierto
      refetchInterval: showResultModal ? false : 60000, // Cada minuto, pero NO si modal está abierto
      staleTime: 30000, // Datos válidos por 30 segundos
      refetchOnWindowFocus: false, // No refetch al enfocar ventana
      refetchOnMount: true, // Solo refetch al montar componente
      refetchIntervalInBackground: false, // No refetch en background
      retry: (failureCount, error) => {
        // No reintentar en rate limiting (429)
        if (error?.response?.status === 429) {
          toast.error('Demasiadas solicitudes. Espera un momento antes de continuar.');
          return false;
        }
        return failureCount < 3;
      }
    }
  );

  const updateResultMutation = useMutation(
    (data) => api.post('/betting/step-result', data),
    {
      onSuccess: (response) => {
        // Invalidar solo las queries específicas necesarias
        queryClient.invalidateQueries('current-bet-total');
        queryClient.invalidateQueries('events');
        queryClient.invalidateQueries('admin-dashboard');

        // Mostrar información sobre participantes eliminados
        if (response.data.removedParticipants && response.data.removedParticipants.length > 0) {
          const eliminatedCount = response.data.removedParticipants.length;
          toast.error(`⚠️ ${eliminatedCount} participante(s) eliminado(s) por fondos insuficientes`, {
            duration: 6000,
          });
        }

        if (response.data.isWin) {
          setShowCelebration(true);
          toast.success('¡🎉 EMPATE! Ganancias calculadas automáticamente');

          setTimeout(() => {
            setShowCelebration(false);
          }, 3000);
        } else {
          const message = response.data.removedParticipants && response.data.removedParticipants.length > 0
            ? `Resultado registrado. ${response.data.activeParticipants} participante(s) continúan.`
            : 'Resultado registrado. Avanzando al siguiente paso.';

          toast.success(message);
        }

        setShowResultModal(false);

        // Refetch manual para actualizar datos después de enviar resultado
        setTimeout(() => {
          queryClient.refetchQueries('current-bet-total');
        }, 500);
      },
      onError: (error) => {
        console.error('❌ Error completo:', error);
        console.error('❌ Error response:', error.response);
        console.error('❌ Error data:', error.response?.data);

        const errorMessage = error.response?.data?.message ||
                             error.response?.data?.error ||
                             error.message ||
                             'Error al actualizar resultado';

        const errorDetails = error.response?.data?.details;
        if (errorDetails) {
          console.error('❌ Detalles de validación:', errorDetails);
          toast.error(`${errorMessage}\nDetalles: ${JSON.stringify(errorDetails)}`);
        } else {
          toast.error(errorMessage);
        }
      }
    }
  );

  const handleResultClick = useCallback(() => {
    setShowResultModal(true);
  }, []);

  const onSubmitResult = useCallback((formData) => {
    // Validar que tenemos los datos necesarios
    if (!bettingData?.eventId || !bettingData?.currentStep) {
      toast.error('Error: Falta información del evento');
      return;
    }

    const data = {
      eventId: parseInt(bettingData.eventId),
      stepNumber: parseInt(bettingData.currentStep),
      result: formData.result,
      matchName: formData.matchName || `Partido Paso ${bettingData.currentStep}`,
      localTeamId: formData.localTeamId,
      visitorTeamId: formData.visitorTeamId,
      localScore: formData.localScore,
      visitorScore: formData.visitorScore,
      drawOdds: formData.drawOdds,
      maxNonDraw: formData.maxNonDraw,
      payoutFactor: formData.payoutFactor
    };

    // Debug logs - puedes comentar estas líneas en producción
    console.log('🔍 Datos enviados al backend:', data);
    console.log('📋 FormData recibido:', formData);
    console.log('🎯 BettingData:', { eventId: bettingData.eventId, currentStep: bettingData.currentStep });

    updateResultMutation.mutate(data);
  }, [bettingData, updateResultMutation]);

  const formatCurrency = (amount) => {
    return `$${Number(amount || 0).toLocaleString()}`;
  };

  const getStepColor = (step) => {
    if (step <= 5) return 'success';
    if (step <= 9) return 'warning';
    return 'danger';
  };

  const getRiskMessage = (step) => {
    if (step <= 5) return '🟢 Zona de Riesgo Bajo';
    if (step <= 9) return '🟡 Zona de Riesgo Medio';
    return '🔴 Zona de Riesgo Alto';
  };

  const handleCloseModal = useCallback(() => {
    setShowResultModal(false);
  }, []);

  const stepResultProps = useMemo(() => ({
    eventId: bettingData?.eventId,
    stepNumber: bettingData?.currentStep,
    onClose: handleCloseModal,
    onSubmit: onSubmitResult,
    isLoading: updateResultMutation.isLoading
  }), [bettingData?.eventId, bettingData?.currentStep, handleCloseModal, onSubmitResult, updateResultMutation.isLoading]);

  const ResultModal = useMemo(() => {
    if (!showResultModal || !bettingData) return null;
    return <StepResultForm {...stepResultProps} />;
  }, [showResultModal, bettingData, stepResultProps]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8 text-primary-600"></div>
      </div>
    );
  }

  if (!bettingData || !bettingData.eventId) {
    return (
      <div className="text-center py-12">
        <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 mb-2">
          No hay eventos activos
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Necesitas tener un evento activo para gestionar apuestas
        </p>
        <a href="/admin/events/new" className="btn-primary">
          Crear Nuevo Evento
        </a>
      </div>
    );
  }

  const { currentStep, totalBetAmount, participantCount, participants } = bettingData;
  const stepColor = getStepColor(currentStep);
  const hasInsufficientFunds = participants.some(p => !p.canBet);

  return (
    <div className="space-y-6">
      <CelebrationAnimation show={showCelebration} />

      <div className="text-center relative">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Gestión de Apuestas
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Actualiza los resultados de los partidos en tiempo real
        </p>

        {/* Botón refrescar */}
        <button
          onClick={() => queryClient.refetchQueries('current-bet-total')}
          disabled={isLoading}
          className="absolute top-0 right-0 p-2 text-gray-500 hover:text-primary-600 disabled:opacity-50"
          title="Refrescar datos"
        >
          <Zap className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card p-8 text-center"
      >
        <div className="mb-6">
          <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 ${
            stepColor === 'success' ? 'bg-success-100 dark:bg-success-900/30' :
            stepColor === 'warning' ? 'bg-warning-100 dark:bg-warning-900/30' :
            'bg-danger-100 dark:bg-danger-900/30'
          }`}>
            <span className={`text-3xl font-bold ${
              stepColor === 'success' ? 'text-success-600 dark:text-success-400' :
              stepColor === 'warning' ? 'text-warning-600 dark:text-warning-400' :
              'text-danger-600 dark:text-danger-400'
            }`}>
              {currentStep}
            </span>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Paso {currentStep} de 13
          </h2>

          <p className={`text-lg font-medium ${
            stepColor === 'success' ? 'text-success-600 dark:text-success-400' :
            stepColor === 'warning' ? 'text-warning-600 dark:text-warning-400' :
            'text-danger-600 dark:text-danger-400'
          }`}>
            {getRiskMessage(currentStep)}
          </p>
        </div>

        <div className="w-full max-w-md mx-auto mb-6">
          <div className="w-full bg-gray-200 dark:bg-dark-700 rounded-full h-3">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${(currentStep / 13) * 100}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
              className={`h-3 rounded-full ${
                stepColor === 'success' ? 'bg-gradient-success' :
                stepColor === 'warning' ? 'bg-gradient-warning' :
                'bg-gradient-danger'
              }`}
            />
          </div>
        </div>

        <div className="mb-8">
          <p className="text-gray-600 dark:text-gray-400 mb-2">
            Monto Total de Apuesta
          </p>
          <p className="text-4xl font-bold text-gray-900 dark:text-gray-100">
            {formatCurrency(totalBetAmount)}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {participantCount} participante{participantCount !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="flex justify-center max-w-md mx-auto">
          <button
            onClick={handleResultClick}
            disabled={hasInsufficientFunds}
            className="w-full btn-primary py-4 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Target className="w-6 h-6 mr-2" />
            Registrar Resultado del Partido
          </button>
        </div>

        {hasInsufficientFunds && (
          <div className="mt-4 p-3 bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg">
            <p className="text-warning-800 dark:text-warning-200 text-sm">
              ⚠️ Algunos clientes no tienen saldo suficiente para este paso
            </p>
          </div>
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Estado de Participantes
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {participants.map((participant) => (
            <div
              key={participant.userId}
              className={`p-4 rounded-lg border-2 ${
                participant.canBet
                  ? 'border-success-200 dark:border-success-800 bg-success-50 dark:bg-success-900/20'
                  : 'border-warning-200 dark:border-warning-800 bg-warning-50 dark:bg-warning-900/20'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-gradient-primary rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-medium">
                      {participant.fullName.charAt(0)}
                    </span>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {participant.fullName}
                  </span>
                </div>

                <div className={`p-1 rounded-full ${
                  participant.canBet
                    ? 'bg-success-500'
                    : 'bg-warning-500'
                }`}>
                  <div className="w-2 h-2 bg-white rounded-full" />
                </div>
              </div>

              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Apuesta:</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {formatCurrency(participant.betAmount)}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Saldo:</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {formatCurrency(participant.currentBalance)}
                  </span>
                </div>

                {!participant.canBet && (
                  <div className="flex justify-between text-warning-700 dark:text-warning-300">
                    <span>Faltante:</span>
                    <span className="font-medium">
                      {formatCurrency(participant.shortfall)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card p-6 text-center"
        >
          <Target className="w-8 h-8 text-primary-600 dark:text-primary-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {currentStep}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Paso Actual
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card p-6 text-center"
        >
          <Users className="w-8 h-8 text-success-600 dark:text-success-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {participants.filter(p => p.canBet).length}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Listos para Apostar
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="card p-6 text-center"
        >
          <DollarSign className="w-8 h-8 text-warning-600 dark:text-warning-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {formatCurrency(totalBetAmount)}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Total a Apostar
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="card p-6 text-center"
        >
          <AlertTriangle className={`w-8 h-8 mx-auto mb-2 ${
            hasInsufficientFunds
              ? 'text-danger-600 dark:text-danger-400'
              : 'text-gray-400'
          }`} />
          <p className={`text-2xl font-bold ${
            hasInsufficientFunds
              ? 'text-danger-600 dark:text-danger-400'
              : 'text-gray-900 dark:text-gray-100'
          }`}>
            {participants.filter(p => !p.canBet).length}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Sin Saldo Suficiente
          </p>
        </motion.div>
      </div>

      {ResultModal}
    </div>
  );
};

export default BettingManagement;